import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import http from '@/services/http'

export type PlatformMode = 'tenant' | 'platform' | 'legacy'

export interface TenantInfo {
  id: number
  slug: string
  nombre: string | null
  status: number | null
}

interface ContextPayload {
  mode: PlatformMode
  tenant: TenantInfo | null
  host: string | null
}

/**
 * Contexto de plataforma resuelto por el backend según el hostname
 * (GET /api/public/context). Determina si la SPA corre sobre el dominio de un
 * bufete (mode=tenant) o sobre el portal de plataforma (mode=platform) para
 * ramificar layouts, navegación y landing.
 *
 * Se hidrata una sola vez al arrancar la app (ver main.ts).
 */
export const usePlatformStore = defineStore('platform', () => {
  const mode = ref<PlatformMode>('tenant')
  const tenant = ref<TenantInfo | null>(null)
  const host = ref<string | null>(null)
  const loaded = ref(false)

  const isPlatform = computed(() => mode.value === 'platform')
  const isTenant = computed(() => mode.value === 'tenant')

  async function fetchContext(): Promise<void> {
    try {
      // El endpoint responde { status, context: {...} } (la clave es `context`).
      const { data } = await http.get<{ status: string; context: ContextPayload }>('/public/context')
      const ctx = data.context
      if (ctx) {
        mode.value = ctx.mode
        tenant.value = ctx.tenant
        host.value = ctx.host
      }
    } catch {
      // Si el contexto no responde, asumimos 'tenant' (comportamiento de bufete).
    } finally {
      loaded.value = true
    }
  }

  return { mode, tenant, host, loaded, isPlatform, isTenant, fetchContext }
})
